import { useAuth0 } from "@auth0/auth0-react";
import { Link } from "react-router-dom";
import LoginButton from "../miscComponents/LoginButton";
import {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useRef,
  useCallback,
} from "react";
import LogoutButton from "../miscComponents/LogoutButton";
import clsx from "clsx";
import ProfileSkeleton from "./ProfileSkeleton";
import EnvolvementThumbnail from "../appComponents/explorePage/EnvolvementThumbnail";

const backend_domain = process.env.REACT_APP_BACKEND_DOMAIN;
const backend_port = process.env.REACT_APP_BACKEND_PORT;

// Banner component with upload functionality
const ProfileHeader = forwardRef(
  (
    {
      user,
      userProfile,
      isAuthenticated,
      getAccessTokenSilently,
      fetchUserProfile,
    },
    ref
  ) => {
    const [bannerUrl, setBannerUrl] = useState(null);
    const [isUploading, setIsUploading] = useState(false);

    // This should be fetched from the user's profile data
    useEffect(() => {
      const custom_claim_banner = "https://pegasus/banner_url";
      if (user && user[custom_claim_banner]) {
        setBannerUrl(user[custom_claim_banner]);
      }
      if (
        userProfile &&
        userProfile.user_metadata &&
        userProfile.user_metadata.banner_url
      ) {
        setBannerUrl(userProfile.user_metadata.banner_url);
      }
    }, [user, userProfile]);

    const handleBannerUpload = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      setIsUploading(true);

      const accessToken = await getAccessTokenSilently();
      const formData = new FormData();
      formData.append("banner", file);

      try {
        const response = await fetch(
          `${backend_domain}${backend_port}/profile/banner`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
            body: formData,
          }
        );

        if (!response.ok) throw new Error("Banner upload failed");

        const data = await response.json();
        setBannerUrl(data.bannerUrl); // Assuming backend returns the new URL
        if (fetchUserProfile) {
          fetchUserProfile();
        }
      } catch (error) {
        console.error("Error uploading banner:", error);
        // TODO: Show an error message to the user
      } finally {
        setIsUploading(false);
      }
    };

    const triggerFileInput = () => {
      if (!isUploading) {
        document.getElementById("bannerInput").click();
      }
    };

    useImperativeHandle(ref, () => ({
      triggerBannerEdit: triggerFileInput,
    }));

    return (
      <div className="relative w-full h-60 bg-gray-200 dark:bg-gray-700 rounded-lg">
        {bannerUrl ? (
          <img
            src={bannerUrl}
            alt="Profile banner"
            className="w-full h-full object-cover rounded-lg"
          />
        ) : (
          <div className="w-full h-full bg-gray-100 rounded-lg" />
        )}
        {isAuthenticated && (
          <>
            {isUploading && (
              <div className="absolute inset-0 bg-black bg-opacity-40 transition-all duration-300 flex items-center justify-center rounded-lg">
                <div className="w-10 h-10 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}
            <input
              type="file"
              id="bannerInput"
              hidden
              accept="image/*"
              onChange={handleBannerUpload}
            />
          </>
        )}
      </div>
    );
  }
);

// Pencil icon button
const EditButton = ({ onClick }) => (
  <button
    onClick={onClick}
    className="absolute top-4 right-4 bg-white border rounded-full p-2 shadow hover:bg-gray-100"
    aria-label="Edit Profile"
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      className="w-6 h-6"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16.862 4.487a2.25 2.25 0 113.182 3.182L7.5 20.213l-4 1 1-4 12.362-12.726z"
      />
    </svg>
  </button>
);

const Profile = () => {
  const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const [userProfile, setUserProfile] = useState(null);
  const [data, setData] = useState([]); // My Envolvements
  const [purchased, setPurchased] = useState([]); // Purchased Envolvements
  const [loading, setLoading] = useState(true);
  const [loadingPurchased, setLoadingPurchased] = useState(false);
  const [activeTab, setActiveTab] = useState("my");
  const bannerRef = useRef(null);

  const fetchUserProfile = useCallback(async () => {
    try {
      const accessToken = await getAccessTokenSilently();
      const response = await fetch(
        `${backend_domain}${backend_port}/user_profile_info/${user.sub}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      if (!response.ok) throw new Error("Network response was not ok");
      const profileData = await response.json();
      setUserProfile(profileData);
    } catch (error) {
      console.error("Error fetching user profile:", error);
    }
  }, [getAccessTokenSilently, user]);

  useEffect(() => {
    if (isAuthenticated && user?.sub) {
      fetchUserProfile();
    }
  }, [fetchUserProfile, isAuthenticated, user?.sub]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const accessToken = await getAccessTokenSilently();
        const response = await fetch(
          `${backend_domain}${backend_port}/profile/${user.sub}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );
        if (!response.ok) throw new Error("Network response was not ok");
        const postData = await response.json();
        setData(postData);
      } catch (error) {
        setData([]);
      } finally {
        setLoading(false);
      }
    };
    if (isAuthenticated && user?.sub) fetchData();
  }, [getAccessTokenSilently, isAuthenticated, user?.sub]);

  useEffect(() => {
    const fetchPurchased = async () => {
      setLoadingPurchased(true);
      try {
        const accessToken = await getAccessTokenSilently();
        // Fetch all envolvements and filter client-side for purchasedUserIDs
        const response = await fetch(
          `${backend_domain}${backend_port}/post/pull`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );
        if (!response.ok) throw new Error("Network response was not ok");
        const allEnvolvements = await response.json();
        const purchasedData = allEnvolvements.filter(
          (e) =>
            Array.isArray(e.purchasedUserIDs) &&
            e.purchasedUserIDs.includes(user.sub)
        );
        setPurchased(purchasedData);
      } catch (error) {
        setPurchased([]);
      } finally {
        setLoadingPurchased(false);
      }
    };
    if (isAuthenticated && user?.sub) fetchPurchased();
  }, [getAccessTokenSilently, isAuthenticated, user?.sub]);

  // Placeholder for edit action
  const handleEditProfile = () => {
    bannerRef.current?.triggerBannerEdit();
  };

  if (!isAuthenticated) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <div className="text-center text-xl p-8">
          <p>No profile found... Would you like to Sign in?</p>
        </div>
        <div className="text-center rounded-lg px-3 py-1.5 text-sm font-semibold leading-6 shadow-sm ring-1 ring-gray-900/10 hover:ring-gray-900/20">
          <LoginButton />
        </div>
      </div>
    );
  }

  // Show skeleton loader if loading either tab
  if (
    (activeTab === "my" && loading) ||
    (activeTab === "purchased" && loadingPurchased)
  ) {
    return <ProfileSkeleton />;
  }

  return (
    <div className="max-w-4xl mx-auto w-full px-2 sm:px-4 md:px-8">
      <ProfileHeader
        ref={bannerRef}
        user={user}
        userProfile={userProfile}
        isAuthenticated={isAuthenticated}
        getAccessTokenSilently={getAccessTokenSilently}
        fetchUserProfile={fetchUserProfile}
      />
      <div className="relative flex flex-col items-center -mt-16 mb-6">
        {user?.picture && (
          <img
            className="border-double border-black border-8 rounded-full w-32 h-32 object-cover bg-white"
            src={user.picture}
            alt={user?.name}
          />
        )}
        <div className="mt-4 flex items-center gap-2">
          <h1 className="text-2xl font-bold text-center">
            {user?.nickname || user?.name}
          </h1>
          {/* Show pencil only if account owner */}
          <EditButton onClick={handleEditProfile} />
        </div>
        {/* <p className="text-gray-500 text-center">{user?.email}</p> */}
        <div className="mt-2">
          <LogoutButton />
        </div>
      </div>
      {/* Tabs */}
      <div className="flex justify-center mb-6">
        <button
          className={clsx(
            "px-4 py-2 font-semibold rounded-t-lg border-b-2 transition-colors",
            activeTab === "my"
              ? "border-primary-500 text-primary-600 bg-gray-100"
              : "border-transparent text-gray-500 hover:text-primary-600"
          )}
          onClick={() => setActiveTab("my")}
        >
          My Envolvements
        </button>
        <button
          className={clsx(
            "px-4 py-2 font-semibold rounded-t-lg border-b-2 transition-colors",
            activeTab === "purchased"
              ? "border-primary-500 text-primary-600 bg-gray-100"
              : "border-transparent text-gray-500 hover:text-primary-600"
          )}
          onClick={() => setActiveTab("purchased")}
        >
          Purchased Envolvements
        </button>
      </div>
      {/* Tab Content */}
      <div className="mb-8">
        {activeTab === "my" && (
          <>
            <h2 className="text-xl font-bold mb-4 text-center">Envolvements</h2>
            {data.length === 0 ? (
              <div className="flex flex-col items-center justify-center min-h-[200px]">
                <p className="text-lg text-gray-600 mb-4 text-center">
                  You have no Envolvements. Would you like to add some?
                </p>
                <Link to="/create">
                  <button className="bg-primary-500 text-white px-6 py-2 rounded-xl font-bold shadow hover:bg-primary-600 transition-colors">
                    Create
                  </button>
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {data.map((envolvement) => (
                  <Link
                    key={envolvement._id}
                    to={`/envolvement/${envolvement._id}`}
                    className="block h-full"
                  >
                    <EnvolvementThumbnail data={envolvement} />
                  </Link>
                ))}
              </div>
            )}
          </>
        )}
        {activeTab === "purchased" && (
          <>
            <h2 className="text-xl font-bold mb-4 text-center">
              Purchased Envolvements
            </h2>
            {purchased.length === 0 ? (
              <div className="flex flex-col items-center justify-center min-h-[200px]">
                <p className="text-lg text-gray-600 mb-4 text-center">
                  You have not purchased any Envolvements yet.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {purchased.map((envolvement) => (
                  <Link
                    key={envolvement._id}
                    to={`/envolvement/${envolvement._id}`}
                    className="block h-full"
                  >
                    <EnvolvementThumbnail data={envolvement} />
                  </Link>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Profile;
